/**
 * IngestionReview — Iter 40 + 40.1 + 40.2 bulk plan-ingestion review screen.
 *
 * Iter 40.2 adds:
 *   - Multi-select checkboxes + bulk apply discipline / revision
 *   - Filter pills: All · Needs review · Failed · Ready · Published
 *   - Keyboard navigation (j/k/e/t/d/r/Enter/p/x/?) — see IngestKeyboardHelp
 *   - Accuracy Audit panel — persistent per-batch ingestion scorecard
 *   - Review session timer + per-row median (data-testid="review-timer")
 *
 * Routes:
 *   /project/:projectId/ingest/:batchId
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { canAdminPlans } from "@/lib/permissions";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, Check, X, AlertTriangle, Layers, Loader2, RefreshCw,
  FileText, RotateCcw, Keyboard, Ban,
} from "lucide-react";
import AccuracyAuditPanel from "@/components/ingest/AccuracyAuditPanel";
import ProjectInfoPanel from "@/components/ingest/ProjectInfoPanel";
import IngestKeyboardHelp from "@/components/ingest/IngestKeyboardHelp";

// Iter 141 — Discipline catalog. Display labels match
// `models.DISCIPLINE_LABELS` (also served by /api/lookups). Internal
// keys stay lowercase_snake; UI shows the labels. Order is the
// professional read-order for heavy-civil plan sets.
const DISCIPLINE_OPTIONS = [
  { value: "cover_sheet",         label: "Cover Sheet" },
  { value: "general",             label: "General" },
  { value: "survey",              label: "Survey" },
  { value: "existing_conditions", label: "Existing Conditions" },
  { value: "demolition",          label: "Demolition" },
  { value: "erosion_control",     label: "Erosion Control" },
  { value: "mot",                 label: "Maintenance of Traffic" },
  { value: "earthwork",           label: "Earthwork" },
  { value: "drainage",            label: "Drainage" },
  { value: "utility",             label: "Utilities" },
  { value: "water",               label: "Water" },
  { value: "sanitary_sewer",      label: "Sanitary Sewer" },
  { value: "storm_sewer",         label: "Storm Sewer" },
  { value: "force_main",          label: "Force Main" },
  { value: "roadway",             label: "Roadway" },
  { value: "grading",             label: "Grading" },
  { value: "paving",              label: "Paving" },
  { value: "signing_striping",    label: "Signing & Pavement Marking" },
  { value: "traffic_signal",      label: "Traffic Signal" },
  { value: "lighting",            label: "Lighting" },
  { value: "electrical",          label: "Electrical" },
  { value: "structures",          label: "Structures" },
  { value: "landscape",           label: "Landscape" },
  { value: "irrigation",          label: "Irrigation" },
  { value: "cross_sections",      label: "Cross Sections" },
  { value: "profiles",            label: "Profiles" },
  { value: "details",             label: "Details" },
  { value: "geotechnical",        label: "Geotechnical" },
  { value: "environmental",       label: "Environmental" },
  { value: "other",               label: "Other" },
];

const CONFIDENCE_TONE = {
  high:   { color: "#10b981", label: "OK" },
  medium: { color: "#f59e0b", label: "Review" },
  low:    { color: "#ef4444", label: "Low" },
  none:   { color: "#6b7d8a", label: "—" },
};

const MATCH_LABEL = {
  new: "New sheet",
  match_exact: "Match",
  match_corrected: "Match · prior fix",
  match_filename: "Match · filename",
  match_title: "Match · title",
  match_discipline_order: "Match · order",
  possible_duplicate: "Possible duplicate",
  needs_review: "Needs review",
};
const MATCH_TONE = {
  match_exact: "primary", match_corrected: "primary", match_title: "amber",
  match_discipline_order: "amber", new: "steel",
  possible_duplicate: "warn", needs_review: "warn",
};

const ACTIVE_STATUSES = new Set(["uploaded", "queued", "processing", "reviewing"]);
const POLL_MS = 1500;

const FILTER_OPTIONS = [
  { id: "all", label: "All" },
  { id: "needs_review", label: "Needs review" },
  { id: "failed", label: "Failed" },
  { id: "ready", label: "Ready" },
  { id: "published", label: "Published" },
];


function Dot({ level, field }) {
  const t = CONFIDENCE_TONE[level] || CONFIDENCE_TONE.none;
  const fieldLabel = field ? `${field} confidence` : "Confidence";
  return (
    <span
      title={`${fieldLabel}: ${level || "unknown"} — reflects how strongly this value is grounded in the source drawing, not an independent accuracy check. Verify against the source before trusting it fully.`}
      className="inline-flex items-center gap-1 text-[9px] font-mono uppercase"
      style={{ color: t.color }}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
      {t.label}
    </span>
  );
}

function StatusPill({ status }) {
  const tones = {
    ready:        { color: "#10b981", label: "Ready",
      title: "Extraction found a printed sheet number and title with enough confidence to publish as-is. This reflects extraction confidence, not independently verified accuracy — review before publishing anything you have doubts about." },
    needs_review: { color: "#f59e0b", label: "Needs review",
      title: "Extraction could not confidently determine the sheet number/title (or found a conflict, such as a duplicate identity within this upload) — confirm or correct the value from the source drawing before this can be published." },
    pending:      { color: "#6b7d8a", label: "Pending", title: "Not yet processed." },
    published:    { color: "#3b82f6", label: "Published", title: "Live as a Sheet — a human has confirmed this identity." },
    discarded:    { color: "#6b7d8a", label: "Discarded", title: "Removed from this batch's review queue." },
    failed:       { color: "#ef4444", label: "Failed", title: "Extraction could not process this page." },
  };
  const t = tones[status] || tones.pending;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono uppercase tracking-wider"
      style={{ color: t.color, border: `1px solid ${t.color}`, background: `${t.color}11` }}
      data-status={status}
      title={t.title}
      data-testid="status-pill"
    >
      {t.label}
    </span>
  );
}

/**
 * 2026-02-14 (identity validation, ForgedOps completion) — surfaces
 * WHY a candidate carries `identity_flag` (duplicate_identity /
 * unsupported_identity), distinct from a plain low-confidence
 * extraction. Only renders when a flag is actually set.
 */
function IdentityFlagPill({ cand }) {
  if (!cand.identity_flag) return null;
  const labels = {
    duplicate_identity: "Duplicate identity",
    unsupported_identity: "Unsupported identity",
  };
  const label = labels[cand.identity_flag] || "Identity conflict";
  return (
    <span
      title={cand.identity_flag_detail || label}
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider"
      style={{ color: "#f87171", border: "1px solid #f87171", background: "#f8717111" }}
      data-testid="identity-flag-pill"
      data-identity-flag={cand.identity_flag}
    >
      {label}
    </span>
  );
}


/**
 * Iter 44 §P3 — Source Quality reviewer pill.
 *
 * One tiny chip beside the StatusPill that hints at *why* a candidate
 * is in review. Operational reviewer-assist only — no dashboard, no
 * analytics, no aggregate reporting. Data already collected by
 * services/scan_classifier on the backend.
 *
 * Render rule: show only when the page kind is non-vector OR the scan
 * quality is sub-good. A clean `embedded_text` page with no scan data
 * shows nothing — there's nothing operationally interesting to report.
 */
function SourceQualityPill({ cand }) {
  // Iter 44 §P3 — purpose is reviewer-assist on candidates IN review,
  // so suppress on ready/published/discarded. Failed rows already carry
  // their own failure_reason copy.
  if (cand.status !== "needs_review") return null;
  const kind = cand.page_kind;
  const quality = cand.scan_quality;
  // Choose the most operationally-relevant single chip.
  // Order of priority: severe/poor scan > raster_only > hybrid > hidden_text
  // > moderate scan. Nothing surfaces for crisp embedded_text + good scan.
  let label = null;
  let tone = "#6b7d8a";
  if (quality === "severe") { label = "scan: severe"; tone = "#ef4444"; }
  else if (quality === "poor") { label = "scan: poor"; tone = "#f59e0b"; }
  else if (kind === "raster_only") { label = "kind: raster_only"; tone = "#f59e0b"; }
  else if (kind === "hybrid") { label = "kind: hybrid"; tone = "#94a3b8"; }
  else if (kind === "hidden_text") { label = "kind: hidden_text"; tone = "#94a3b8"; }
  else if (quality === "moderate") { label = "scan: moderate"; tone = "#94a3b8"; }
  if (!label) return null;
  return (
    <span
      className="inline-flex items-center px-1.5 py-[1px] rounded-sm text-[9px] font-mono uppercase tracking-wider ml-1 align-top"
      style={{ color: tone, border: `1px solid ${tone}55`, background: `${tone}0c` }}
      title="Source quality — explains why this candidate may need review"
      data-testid={`source-quality-${cand.id}`}
    >
      {label}
    </span>
  );
}


function ProcessingBanner({ batch }) {
  const counts = batch.counts || {};
  const total = counts.total ?? batch.page_count ?? 0;
  const processed = counts.processed ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const ready = counts.ready ?? 0;
  const needs = counts.needs_review ?? 0;
  const failed = counts.failed ?? 0;
  // Iter 140 — durable job lifecycle: queued → processing → reviewing.
  const isQueued = batch.status === "queued";
  const isAwaitingReview = batch.status === "reviewing" && processed >= total && total > 0;
  const label = isQueued
    ? "Queued for processing"
    : isAwaitingReview
    ? "Awaiting review"
    : "Processing Plan Set";
  return (
    <div
      className="mb-4 border border-[var(--fop-border)] rounded-md p-3 bg-[var(--fop-surface-2)]"
      data-testid="ingest-processing-banner"
      data-batch-status={batch.status}
      data-awaiting-review={isAwaitingReview ? "1" : "0"}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)]">
          {!isAwaitingReview && (
            <Loader2 size={12} className="animate-spin text-[var(--fop-primary)]" />
          )}
          {label}
          <span className="text-[var(--fop-text-3)]">·</span>
          <span data-testid="ingest-progress-count">{processed} / {total} sheets processed</span>
        </div>
        <div className="inline-flex items-center gap-3 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          <span><span className="text-emerald-400">Ready:</span> {ready}</span>
          <span><span className="text-amber-400">Needs review:</span> {needs}</span>
          <span><span className="text-red-400">Failed:</span> {failed}</span>
        </div>
      </div>
      <div
        className="h-1 w-full bg-[var(--fop-border)] rounded overflow-hidden"
        role="progressbar"
        aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
      >
        <div
          className="h-full bg-[var(--fop-primary)] transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
          data-testid="ingest-progress-bar"
          data-progress-pct={pct}
        />
      </div>
    </div>
  );
}


// Iter 140 — terminal-state banner for partial batches. When a batch
// ends with `partial` (mixed success/failure), the user MUST see "N
// pages failed" explicitly. The old UI rendered everything as "Ready
// to publish" which hid the failed pages.
function PartialBatchBanner({ batch }) {
  const counts = batch.counts || {};
  const failed = counts.failed ?? 0;
  const published = counts.published ?? 0;
  if (batch.status !== "partial" || failed <= 0) return null;
  const startedAt = batch.processing_started_at;
  const finishedAt = batch.processing_finished_at;
  let duration = null;
  if (startedAt && finishedAt) {
    try {
      duration = Math.max(0, Math.round(
        (new Date(finishedAt).getTime() - new Date(startedAt).getTime()) / 1000,
      ));
    } catch { duration = null; }
  }
  return (
    <div
      className="mb-4 border border-amber-500/40 bg-amber-500/5 rounded-md p-3"
      data-testid="ingest-partial-banner"
      data-batch-status={batch.status}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-400 mb-1">
        Partial batch · pages failed
      </div>
      <div className="text-sm text-[var(--fop-text)]">
        <strong className="text-amber-400">{failed}</strong> page{failed === 1 ? "" : "s"} failed to extract.
        {" "}<strong className="text-emerald-400">{published}</strong> succeeded.
        {duration !== null && (
          <span className="ml-2 text-[var(--fop-text-3)] font-mono text-xs">
            ({duration}s processing)
          </span>
        )}
      </div>
      <div className="text-xs text-[var(--fop-text-3)] mt-1">
        Filter to &quot;Failed&quot; below to see each page + reason. A platform admin can run
        <code className="mx-1 px-1 py-0.5 bg-[var(--fop-surface)] rounded font-mono text-[11px]">reprocess</code>
        to retry failed pages.
      </div>
    </div>
  );
}


function ReviewTimer({ rowsCorrected }) {
  const [start] = useState(() => Date.now());
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, []);
  const elapsed = Math.max(1, Math.round((Date.now() - start) / 1000));
  const min = Math.floor(elapsed / 60);
  const sec = String(elapsed % 60).padStart(2, "0");
  const median = rowsCorrected > 0 ? Math.round(elapsed / rowsCorrected) : null;
  return (
    <span
      className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]"
      data-testid="review-timer"
      data-elapsed-sec={elapsed}
      data-corrected={rowsCorrected}
      data-median-sec={median ?? ""}
    >
      Session {min}:{sec}{rowsCorrected > 0 ? ` · ${median}s/row` : ""}
      {/* keep tick referenced so eslint doesn't complain */}
      <span hidden>{tick}</span>
    </span>
  );
}


export default function IngestionReview() {
  const { projectId, batchId } = useParams();
  const { user } = useAuth();
  const [batch, setBatch] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [filter, setFilter] = useState("all");
  const [showKbd, setShowKbd] = useState(false);
  const [focusRow, setFocusRow] = useState(null);  // candidate id of keyboard focus
  const [auditTick, setAuditTick] = useState(0);
  const [correctedSession, setCorrectedSession] = useState(0);
  const pollTimerRef = useRef(null);
  const showAdmin = canAdminPlans(user);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      // Iter 44 §P2 — long timeout + single retry. During heavy OCR
      // (turnbull/clement deep-regional pass) a polling GET can take
      // 30-60s. The default 30s timeout on the axios instance would
      // bounce these; explicit 120s here keeps the UI smoothly polling
      // through OCR-saturated batches.
      let attempt = 0;
      let lastErr;
      while (attempt < 2) {
        try {
          const { data } = await api.get(`/upload_batches/${batchId}`, {
            timeout: 120000,
          });
          setBatch(data.batch);
          setCandidates(data.candidates || []);
          return data.batch;
        } catch (e) {
          lastErr = e;
          const isTimeout = e?.code === "ECONNABORTED" ||
                            e?.message?.toLowerCase().includes("timeout");
          if (!isTimeout) throw e;
          attempt += 1;
        }
      }
      throw lastErr;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [batchId]);

  useEffect(() => {
    const clear = () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
    if (!batch) return clear;
    if (!ACTIVE_STATUSES.has(batch.status)) return clear;
    pollTimerRef.current = setTimeout(() => load({ silent: true }), POLL_MS);
    return clear;
  }, [batch, batchId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh accuracy panel when corrections happen
  const bumpAudit = useCallback(() => setAuditTick((t) => t + 1), []);

  const filtered = useMemo(() => {
    if (filter === "all") return candidates;
    return candidates.filter((c) => c.status === filter);
  }, [candidates, filter]);

  const readyCount = useMemo(() => candidates.filter((c) => c.status === "ready").length, [candidates]);
  const reviewCount = useMemo(() => candidates.filter((c) => c.status === "needs_review").length, [candidates]);
  const failedCount = useMemo(() => candidates.filter((c) => c.status === "failed").length, [candidates]);
  const isProcessing = batch && ACTIVE_STATUSES.has(batch.status);

  // ---- selection helpers ----
  const toggleOne = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allFilteredIds = filtered.map((c) => c.id);
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every((id) => selected.has(id));
  const toggleAllFiltered = () => setSelected((s) => {
    if (allSelected) {
      const n = new Set(s);
      allFilteredIds.forEach((id) => n.delete(id));
      return n;
    }
    const n = new Set(s);
    allFilteredIds.forEach((id) => n.add(id));
    return n;
  });
  const clearSelection = () => setSelected(new Set());

  // ---- API mutations ----
  const handleBulkPublish = async () => {
    setPublishing(true);
    try { await api.post(`/upload_batches/${batchId}/publish`); await load(); bumpAudit(); }
    finally { setPublishing(false); }
  };

  const handlePublishOne = async (cand) => {
    setSavingId(cand.id);
    try {
      await api.post(`/upload_batches/${batchId}/candidates/${cand.id}/publish`);
      await load(); bumpAudit();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Publish failed";
      setCandidates((cs) => cs.map((c) => c.id === cand.id ? { ...c, _error: detail } : c));
    } finally { setSavingId(null); }
  };

  const handleDiscard = async (cand) => {
    setSavingId(cand.id);
    try {
      const fd = new FormData(); fd.append("reason", "manual");
      await api.post(`/upload_batches/${batchId}/candidates/${cand.id}/discard`, fd);
      await load(); bumpAudit();
    } finally { setSavingId(null); }
  };

  const handleRetry = async (cand) => {
    setSavingId(cand.id);
    try {
      await api.post(`/upload_batches/${batchId}/candidates/${cand.id}/retry`);
      await load(); bumpAudit();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Retry failed";
      setCandidates((cs) => cs.map((c) => c.id === cand.id ? { ...c, _error: detail } : c));
    } finally { setSavingId(null); }
  };

  const patchCandidate = async (candId, updates) => {
    setSavingId(candId);
    try {
      const { data } = await api.patch(`/upload_batches/${batchId}/candidates/${candId}`, updates);
      setCandidates((cs) => cs.map((c) => c.id === candId ? data : c));
      setCorrectedSession((n) => n + 1);
      bumpAudit();
    } finally { setSavingId(null); }
  };

  const bulkApply = async (fields) => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    setPublishing(true);
    try {
      await api.post(`/upload_batches/${batchId}/candidates/bulk_update`, {
        candidate_ids: ids, ...fields,
      });
      await load(); bumpAudit();
      clearSelection();
    } finally { setPublishing(false); }
  };

  // ---- keyboard navigation ----
  useEffect(() => {
    const onKey = (e) => {
      // Ignore when typing in inputs/selects (let those handle keys natively)
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag)) {
        // Enter inside an input → blur (saves) + advance focus to next needs_review row
        if (e.key === "Enter" && tag === "input") {
          e.target.blur();
          // advance after blur tick
          setTimeout(() => {
            const next = filtered.find((c) => c.status === "needs_review" && c.id !== focusRow);
            if (next) {
              setFocusRow(next.id);
              const el = document.querySelector(`[data-testid="ingest-num-${next.id}"]`);
              if (el) el.focus();
            }
          }, 50);
        }
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const list = filtered;
      const curIdx = list.findIndex((c) => c.id === focusRow);
      const focusInput = (id, fieldTestid) => {
        const el = document.querySelector(`[data-testid="${fieldTestid}-${id}"]`);
        if (el && el.focus) el.focus();
      };
      switch (e.key) {
        case "j": {
          e.preventDefault();
          const nextIdx = Math.min(list.length - 1, (curIdx < 0 ? 0 : curIdx + 1));
          if (list[nextIdx]) setFocusRow(list[nextIdx].id);
          break;
        }
        case "k": {
          e.preventDefault();
          const prevIdx = Math.max(0, (curIdx < 0 ? 0 : curIdx - 1));
          if (list[prevIdx]) setFocusRow(list[prevIdx].id);
          break;
        }
        case "e": if (focusRow) { e.preventDefault(); focusInput(focusRow, "ingest-num"); } break;
        case "t": if (focusRow) { e.preventDefault(); focusInput(focusRow, "ingest-title"); } break;
        case "d": if (focusRow) { e.preventDefault(); focusInput(focusRow, "ingest-disc"); } break;
        case "r": if (focusRow) { e.preventDefault(); focusInput(focusRow, "ingest-rev"); } break;
        case "p": {
          if (!focusRow) break;
          e.preventDefault();
          const c = list.find((x) => x.id === focusRow);
          if (c && c.status === "ready" && c.match_kind !== "possible_duplicate") handlePublishOne(c);
          break;
        }
        case "x": {
          if (!focusRow) break;
          e.preventDefault();
          const c = list.find((x) => x.id === focusRow);
          if (c && c.status !== "published") handleDiscard(c);
          break;
        }
        case "?": e.preventDefault(); setShowKbd(true); break;
        case "Escape": setShowKbd(false); break;
        default: break;
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [filtered, focusRow]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !batch) {
    return (
      <div className="p-8 text-[var(--fop-text-2)]">
        <Loader2 className="animate-spin inline mr-2" size={14} /> Loading ingestion batch…
      </div>
    );
  }
  if (!batch) {
    return <div className="p-8 text-[var(--fop-text-2)]">Batch not found.</div>;
  }
  if (!showAdmin) {
    return <div className="p-8 text-[var(--fop-text-2)]">Authorized access only.</div>;
  }
  if (batch.status === "failed") {
    return (
      <div className="p-8 max-w-[800px]">
        <div className="border border-red-500/40 bg-red-500/5 rounded-md p-4" data-testid="ingest-batch-failed">
          <div className="font-mono text-xs uppercase tracking-wider text-red-400 mb-1">Batch failed</div>
          <div className="text-sm text-[var(--fop-text)]">{batch.error || "Unknown error"}</div>
          <Link to={`/project/${projectId}`} className="inline-block mt-3 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]">
            ← Back to project
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="ingestion-review-page" data-batch-status={batch.status}>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Link to={`/project/${projectId}`} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]">
          <ArrowLeft size={12} /> Back to project
        </Link>
        <div className="inline-flex items-center gap-3">
          <ReviewTimer rowsCorrected={correctedSession} />
          <button type="button" onClick={() => setShowKbd(true)} className="btn btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="ingest-keyboard-toggle" title="Keyboard shortcuts (?)">
            <Keyboard size={12} /> Shortcuts
          </button>
          <button type="button" onClick={() => load()} className="btn btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="ingest-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      <div className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-steel)]">
          Plan Set Ingestion · {batch.mode === "revision" ? "Revision upload" : "New sheets"}
        </div>
        <h1 className="font-display text-3xl text-[var(--fop-text)] mt-0.5 inline-flex items-center gap-2">
          <FileText size={20} className="text-[var(--fop-text-3)]" />
          {batch.source_file_name}
        </h1>
        <div className="text-xs text-[var(--fop-text-3)] mt-1 font-mono">
          {batch.page_count} pages · batch <span className="text-[var(--fop-text-2)]">{batch.id}</span>
        </div>
      </div>

      {isProcessing && <ProcessingBanner batch={batch} />}
      <PartialBatchBanner batch={batch} />

      <AccuracyAuditPanel batchId={batchId} refreshTick={auditTick} />
      <ProjectInfoPanel batchId={batchId} projectId={projectId} />

      {/* Counts strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
        <Tile label="Total" value={batch.counts?.total ?? batch.page_count ?? 0} />
        <Tile label="High confidence" value={batch.counts?.high_confidence ?? 0} tone="ok" />
        <Tile label="Needs review" value={batch.counts?.needs_review ?? 0} tone="amber" />
        <Tile label="Published" value={batch.counts?.published ?? 0} tone="primary" />
        <Tile label="Failed" value={batch.counts?.failed ?? 0} tone={failedCount > 0 ? "warn" : "steel"} />
      </div>

      {/* Filter pills */}
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="ingest-filter-bar">
        {FILTER_OPTIONS.map((opt) => {
          const count = opt.id === "all" ? candidates.length : candidates.filter((c) => c.status === opt.id).length;
          const active = filter === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setFilter(opt.id)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${active ? "bg-[var(--fop-cta-bg)] text-white border-[var(--fop-cta-bg)]" : "border-[var(--fop-border)] text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"}`}
              data-testid={`ingest-filter-${opt.id}`}
              data-active={active ? "1" : "0"}
            >
              {opt.label} <span className="opacity-60">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Bulk action bar */}
      <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="ingest-bulk-bar">
        <button
          type="button"
          disabled={readyCount === 0 || publishing}
          onClick={handleBulkPublish}
          className="btn btn-primary inline-flex items-center gap-1.5 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="ingest-publish-ready"
        >
          {publishing && <Loader2 size={12} className="animate-spin" />}
          <Check size={12} /> Publish {readyCount} ready
        </button>
        {selected.size > 0 && (
          <BulkApplyControl
            count={selected.size}
            onApply={bulkApply}
            onClear={clearSelection}
            disabled={publishing}
          />
        )}
        <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          {reviewCount > 0
            ? `${reviewCount} need correction before they can publish`
            : (isProcessing ? "Awaiting more pages…" : "All clear")}
        </div>
      </div>

      {/* Candidate table */}
      <div className="border border-[var(--fop-border)] rounded-md overflow-hidden">
        <table className="w-full text-xs" data-testid="ingest-candidate-table">
          <thead>
            <tr className="bg-[var(--fop-surface-2)] text-[var(--fop-text-3)] font-mono text-[10px] uppercase tracking-wider">
              <th className="text-left p-2 w-[28px]">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAllFiltered}
                  aria-label="Select all visible"
                  data-testid="ingest-select-all"
                />
              </th>
              <th className="text-left p-2 w-[48px]">Pg</th>
              <th className="text-left p-2 w-[64px]">Thumb</th>
              <th className="text-left p-2">Sheet #</th>
              <th className="text-left p-2">Title</th>
              <th className="text-left p-2 w-[120px]">Discipline</th>
              <th className="text-left p-2 w-[70px]">Rev</th>
              <th className="text-left p-2 w-[170px]">Match</th>
              <th className="text-left p-2 w-[110px]">Status</th>
              <th className="text-right p-2 w-[170px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <CandidateRow
                key={c.id}
                cand={c}
                batchId={batchId}
                selected={selected.has(c.id)}
                focused={focusRow === c.id}
                onToggleSelect={() => toggleOne(c.id)}
                onFocus={() => setFocusRow(c.id)}
                onPatch={(updates) => patchCandidate(c.id, updates)}
                onPublish={() => handlePublishOne(c)}
                onDiscard={() => handleDiscard(c)}
                onRetry={() => handleRetry(c)}
                saving={savingId === c.id}
              />
            ))}
            {!filtered.length && (
              <tr>
                <td colSpan={10} className="p-4 text-center text-[var(--fop-text-3)]">
                  {isProcessing
                    ? <span className="inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> First sheets landing shortly…</span>
                    : (candidates.length === 0 ? "No pages." : `No rows match filter: ${filter}`)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showKbd && <IngestKeyboardHelp onClose={() => setShowKbd(false)} />}
    </div>
  );
}


function BulkApplyControl({ count, onApply, onClear, disabled }) {
  const [disc, setDisc] = useState("");
  const [rev, setRev] = useState("");
  return (
    <div
      className="inline-flex items-center gap-2 px-2 py-1 rounded border border-[var(--fop-primary)]/40 bg-[var(--fop-primary-soft)]/30"
      data-testid="ingest-bulk-apply"
      data-selected-count={count}
    >
      <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text)]">
        {count} selected
      </span>
      <select
        value={disc}
        onChange={(e) => setDisc(e.target.value)}
        className="text-xs"
        data-testid="ingest-bulk-disc"
      >
        <option value="">Apply discipline…</option>
        {DISCIPLINE_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
      </select>
      <button
        type="button"
        disabled={!disc || disabled}
        onClick={() => { onApply({ discipline: disc }); setDisc(""); }}
        className="px-2 py-0.5 rounded text-[11px] font-mono uppercase tracking-wider border border-[var(--fop-primary)] text-[var(--fop-primary)] hover:bg-[var(--fop-primary-soft)] disabled:opacity-40"
        data-testid="ingest-bulk-disc-apply"
      >Apply</button>
      <input
        value={rev}
        onChange={(e) => setRev(e.target.value)}
        placeholder="rev"
        className="text-xs font-mono w-12"
        data-testid="ingest-bulk-rev"
      />
      <button
        type="button"
        disabled={!rev.trim() || disabled}
        onClick={() => { onApply({ revision: rev.trim() }); setRev(""); }}
        className="px-2 py-0.5 rounded text-[11px] font-mono uppercase tracking-wider border border-[var(--fop-primary)] text-[var(--fop-primary)] hover:bg-[var(--fop-primary-soft)] disabled:opacity-40"
        data-testid="ingest-bulk-rev-apply"
      >Apply</button>
      <button
        type="button"
        onClick={onClear}
        className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
        data-testid="ingest-bulk-clear"
      >Clear</button>
    </div>
  );
}


function Tile({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: { color: "var(--fop-text)" },
    ok:      { color: "#10b981" },
    amber:   { color: "#f59e0b" },
    primary: { color: "var(--fop-primary)" },
    warn:    { color: "#ef4444" },
    steel:   { color: "var(--fop-steel)" },
  };
  return (
    <div className="border border-[var(--fop-border)] rounded-md p-2.5 bg-[var(--fop-surface)]">
      <div className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)]">{label}</div>
      <div className="font-display text-xl mt-0.5" style={{ color: tones[tone].color }}>{value}</div>
    </div>
  );
}


function CandidateRow({ cand, batchId, selected, focused, onToggleSelect, onFocus, onPatch, onPublish, onDiscard, onRetry, saving }) {
  const [editing, setEditing] = useState({
    sheet_number: cand.sheet_number || "",
    sheet_title: cand.sheet_title || "",
    discipline: cand.discipline || "general",
    revision: cand.revision || "0",
  });
  useEffect(() => {
    setEditing({
      sheet_number: cand.sheet_number || "",
      sheet_title: cand.sheet_title || "",
      discipline: cand.discipline || "general",
      revision: cand.revision || "0",
    });
  }, [cand.sheet_number, cand.sheet_title, cand.discipline, cand.revision]);

  const fc = cand.field_confidence || {};
  const thumbSrc = cand.thumb_url
    ? `${process.env.REACT_APP_BACKEND_URL}/api/upload_batches/${batchId}/candidates/${cand.id}/thumb`
    : null;
  const isPublished = cand.status === "published";
  const isDiscarded = cand.status === "discarded";
  const isReady = cand.status === "ready";
  const isFailed = cand.status === "failed";
  const isLocked = isPublished || isDiscarded || isFailed;

  const blurSave = (field) => () => {
    const val = editing[field];
    if (val !== (cand[field] || "")) onPatch({ [field]: val });
  };

  const focusRing = focused ? "ring-1 ring-[var(--fop-primary)]/60" : "";

  if (isFailed) {
    return (
      <tr
        className={`border-t border-red-500/30 bg-red-500/5 ${focusRing}`}
        data-testid={`ingest-row-${cand.id}`}
        data-row-failed="1"
        data-focused={focused ? "1" : "0"}
        onClick={onFocus}
      >
        <td className="p-2 align-top">
          <input
            type="checkbox" checked={selected} onChange={onToggleSelect}
            disabled
            aria-label="row select disabled (failed)"
          />
        </td>
        <td className="p-2 align-top font-mono text-[var(--fop-text-3)]">{cand.source_page_number}</td>
        <td className="p-2 align-top">
          <div className="w-12 h-16 rounded border border-red-500/40 bg-black/30 flex items-center justify-center">
            <AlertTriangle size={16} className="text-red-400" />
          </div>
        </td>
        <td colSpan={5} className="p-2 align-top">
          <div className="text-[11px] font-mono uppercase tracking-wider text-red-400 mb-0.5">
            Page processing failed
          </div>
          <div className="text-xs text-[var(--fop-text-2)]" data-testid={`ingest-fail-reason-${cand.id}`}>
            {cand.failure_reason || "Unknown error"}
          </div>
        </td>
        <td className="p-2 align-top"><StatusPill status="failed" /></td>
        <td className="p-2 align-top text-right">
          <div className="inline-flex items-center gap-1">
            <button
              type="button" onClick={onRetry} disabled={saving}
              className="px-2 py-1 rounded text-[11px] font-mono uppercase tracking-wider border border-[var(--fop-primary)] text-[var(--fop-primary)] hover:bg-[var(--fop-primary-soft)] transition-colors disabled:opacity-40"
              data-testid={`ingest-retry-${cand.id}`}
              title="Re-extract this page from the source PDF"
            ><RotateCcw size={11} /></button>
            <button
              type="button" onClick={onDiscard} disabled={saving}
              className="px-2 py-1 rounded text-[11px] font-mono uppercase tracking-wider border border-[var(--fop-border-2)] text-[var(--fop-text-3)] hover:text-red-400 hover:border-red-400 transition-colors disabled:opacity-40"
              data-testid={`ingest-discard-${cand.id}`}
              title="Discard"
            ><X size={11} /></button>
          </div>
          {cand._error && (<div className="text-[10px] text-red-400 mt-1 font-mono">{cand._error}</div>)}
        </td>
      </tr>
    );
  }

  return (
    <tr
      className={`border-t border-[var(--fop-border)] hover:bg-[var(--fop-surface)]/40 ${focusRing}`}
      data-testid={`ingest-row-${cand.id}`}
      data-focused={focused ? "1" : "0"}
      onClick={onFocus}
    >
      <td className="p-2 align-top">
        <input
          type="checkbox" checked={selected} onChange={onToggleSelect}
          disabled={isLocked}
          aria-label={`Select page ${cand.source_page_number}`}
          data-testid={`ingest-select-${cand.id}`}
        />
      </td>
      <td className="p-2 align-top font-mono text-[var(--fop-text-3)]">{cand.source_page_number}</td>
      <td className="p-2 align-top">
        {thumbSrc ? (
          <img src={thumbSrc} alt={`page ${cand.source_page_number}`}
            className="w-12 h-16 object-cover rounded border border-[var(--fop-border)] bg-black/30"
            loading="lazy" data-testid={`ingest-thumb-${cand.id}`}
          />
        ) : (
          <div className="w-12 h-16 rounded border border-[var(--fop-border)] bg-black/30" />
        )}
      </td>
      <td className="p-2 align-top">
        <div className="flex items-center justify-between mb-0.5">
          <span className="font-mono text-[9px] text-[var(--fop-text-4)]">number</span>
          <Dot level={fc.sheet_number} field="Sheet number" />
        </div>
        <input
          value={editing.sheet_number}
          onChange={(e) => setEditing((f) => ({ ...f, sheet_number: e.target.value.toUpperCase() }))}
          onBlur={blurSave("sheet_number")}
          disabled={isLocked}
          className="w-full font-mono text-xs"
          data-testid={`ingest-num-${cand.id}`}
        />
        {cand.sheet_number_is_app_ref ? (
          <div
            className="mt-0.5 text-[9px] font-mono uppercase tracking-wider text-amber-400"
            data-testid={`ingest-app-ref-badge-${cand.id}`}
            title="No printed drawing number was found on this page. This is an application-generated reference, not a printed sheet number."
          >
            App ref · no printed number
          </div>
        ) : (
          !editing.sheet_number && !isLocked && (
            <button
              type="button"
              onClick={() => onPatch({ confirm_no_printed_number: true })}
              disabled={saving}
              className="mt-0.5 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] hover:text-amber-400 transition-colors disabled:opacity-40"
              data-testid={`ingest-confirm-no-number-${cand.id}`}
              title="Confirm this page (e.g. a cover sheet) has no printed drawing number — assigns a stable app reference instead"
            ><Ban size={9} /> No printed number</button>
          )
        )}
      </td>
      <td className="p-2 align-top">
        <div className="flex items-center justify-between mb-0.5">
          <span className="font-mono text-[9px] text-[var(--fop-text-4)]">title</span>
          <Dot level={fc.sheet_title} field="Sheet title" />
        </div>
        <input
          value={editing.sheet_title}
          onChange={(e) => setEditing((f) => ({ ...f, sheet_title: e.target.value }))}
          onBlur={blurSave("sheet_title")}
          disabled={isLocked}
          className="w-full text-xs"
          data-testid={`ingest-title-${cand.id}`}
        />
        {cand.suggested_description && cand.description_recommendation_status === "pending" && !isLocked && (
          <div
            className="mt-1 text-[9px] font-mono text-[var(--fop-text-4)] border border-[var(--fop-border)] rounded-sm px-1.5 py-1"
            data-testid={`ingest-description-suggestion-${cand.id}`}
            title={cand.suggested_description_source === "template_generated"
              ? "Inferred placeholder — no printed caption text was found on this page; this is a generic label, not a recovered title"
              : cand.suggested_description_completeness === "partial"
                ? `Partial caption recovered from nearby page text — may be a fragment of a longer printed title: "${cand.suggested_description_evidence || ""}"`
                : `Complete printed title recovered from nearby page text: "${cand.suggested_description_evidence || ""}"`}
          >
            <div className="uppercase tracking-wider text-amber-400 mb-0.5">
              {cand.suggested_description_source === "template_generated"
                ? "Inferred placeholder (no printed evidence)"
                : cand.suggested_description_completeness === "partial"
                  ? "Partial caption recovered"
                  : "Printed title recovered"}
            </div>
            <div className="text-[var(--fop-text-3)] mb-1 italic">&ldquo;{cand.suggested_description}&rdquo;</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onPatch({ accept_description_recommendation: true })}
                disabled={saving}
                className="text-emerald-400 hover:text-emerald-300 uppercase disabled:opacity-40"
                data-testid={`ingest-description-accept-${cand.id}`}
              >Accept</button>
              <button
                type="button"
                onClick={() => onPatch({ reject_description_recommendation: true })}
                disabled={saving}
                className="text-[var(--fop-text-4)] hover:text-[var(--fop-text)] uppercase disabled:opacity-40"
                data-testid={`ingest-description-reject-${cand.id}`}
              >Reject</button>
            </div>
          </div>
        )}
      </td>
      <td className="p-2 align-top">
        <select
          value={editing.discipline}
          onChange={(e) => { setEditing((f) => ({ ...f, discipline: e.target.value })); onPatch({ discipline: e.target.value }); }}
          disabled={isLocked}
          className="w-full text-xs"
          data-testid={`ingest-disc-${cand.id}`}
        >
          {DISCIPLINE_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
      </td>
      <td className="p-2 align-top">
        <input
          value={editing.revision}
          onChange={(e) => setEditing((f) => ({ ...f, revision: e.target.value }))}
          onBlur={blurSave("revision")}
          disabled={isLocked}
          className="w-full font-mono text-xs"
          data-testid={`ingest-rev-${cand.id}`}
        />
      </td>
      <td className="p-2 align-top">
        <MatchPill kind={cand.match_kind} matched_to={cand.matched_sheet_number} matched_rev={cand.matched_revision} incoming_rev={cand.revision} />
      </td>
      <td className="p-2 align-top">
        <StatusPill status={cand.status} />
        <IdentityFlagPill cand={cand} />
        <SourceQualityPill cand={cand} />
      </td>
      <td className="p-2 align-top text-right">
        {!isLocked && (
          <div className="inline-flex items-center gap-1">
            <button
              type="button" onClick={onPublish}
              disabled={!isReady || saving || cand.match_kind === "possible_duplicate"}
              className="px-2 py-1 rounded text-[11px] font-mono uppercase tracking-wider border border-[var(--fop-primary)] text-[var(--fop-primary)] hover:bg-[var(--fop-primary-soft)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`ingest-publish-${cand.id}`}
              title="Publish to live plan set"
            ><Check size={11} /></button>
            <button
              type="button" onClick={onDiscard} disabled={saving}
              className="px-2 py-1 rounded text-[11px] font-mono uppercase tracking-wider border border-[var(--fop-border-2)] text-[var(--fop-text-3)] hover:text-red-400 hover:border-red-400 transition-colors disabled:opacity-40"
              data-testid={`ingest-discard-${cand.id}`}
              title="Discard"
            ><X size={11} /></button>
          </div>
        )}
        {isPublished && cand.published_sheet_id && (
          <a href={`/sheet/${cand.published_sheet_id}`} className="text-[11px] font-mono uppercase tracking-wider text-emerald-400 hover:underline">View →</a>
        )}
        {cand._error && (<div className="text-[10px] text-red-400 mt-1 font-mono">{cand._error}</div>)}
      </td>
    </tr>
  );
}


function MatchPill({ kind, matched_to, matched_rev, incoming_rev }) {
  const tone = MATCH_TONE[kind] || "neutral";
  const TONE = {
    primary: { color: "var(--fop-primary)" },
    amber:   { color: "#f59e0b" },
    warn:    { color: "#ef4444" },
    steel:   { color: "var(--fop-steel)" },
    neutral: { color: "var(--fop-text-3)" },
  };
  const t = TONE[tone];
  return (
    <div className="inline-flex flex-col gap-0.5">
      <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase" style={{ color: t.color }}>
        {kind === "possible_duplicate" ? <AlertTriangle size={11} /> : <Layers size={11} />}
        {MATCH_LABEL[kind] || kind}
      </span>
      {matched_to && (
        <span className="text-[9px] font-mono text-[var(--fop-text-3)]">
          → {matched_to} (rev {matched_rev}) ↦ {incoming_rev}
        </span>
      )}
    </div>
  );
}
